import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AddBranch, ProfileGet, UpdateBranch, DeleteBranch, GetBranches } from '../../apis/api/ProfileApi';
import Cookies from "js-cookie";
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import AddNewBranchModal from '../../Models/AddNewBranchModel/AddNewBranchModal';
import EditBranchModal from '../../Models/AddNewBranchModel/EditBranchModal';
import './BranchManagement.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

export default function BranchManagement({ readOnly = false, company_id: propCompanyId, userId: propUserId }) {
  const location = useLocation();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const markersLayerRef = useRef(null);

  const dispatch = useDispatch();
  const cookieUserId = Cookies.get("userID");
  const cookieCompanyId = Cookies.get("company_id");

  const effectiveCompanyId =
    propCompanyId != null && propCompanyId !== ""
      ? propCompanyId
      : (location.state?.company_id != null && location.state?.company_id !== ""
          ? location.state.company_id
          : cookieCompanyId);

  const effectiveUserId =
    propUserId != null && propUserId !== ""
      ? propUserId
      : (location.state?.userId != null && location.state?.userId !== ""
          ? location.state.userId
          : cookieUserId);

  const profileData = useSelector((state) => state.ProfileSlice.profileData);

  useEffect(() => {
    if (!effectiveCompanyId) {
      setIsLoading(false);
      setBranches([]);
      setSelectedBranch(null);
      return;
    }

    if (!readOnly && (!profileData?.data || String(profileData?.data?.company_data?.id) !== String(effectiveCompanyId))) {
      dispatch(ProfileGet({ userId: effectiveUserId, company_id: effectiveCompanyId }));
    }

    setIsLoading(true);
    dispatch(GetBranches({ company_id: effectiveCompanyId }))
      .unwrap()
      .then(res => {
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          const approved = res.data.filter(b => b.approve === 1);
          setBranches(approved);
          setSelectedBranch(approved[0] || null);
        } else {
          setBranches([]);
          setSelectedBranch(null);
        }
      })
      .catch(err => {
        console.error("Error fetching branches:", err);
        setBranches([]);
        setSelectedBranch(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, effectiveCompanyId, effectiveUserId, readOnly]);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      const map = L.map(mapRef.current, { zoomControl: false, scrollWheelZoom: true }).setView([25, 55], 3);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      L.control.zoom({ position: 'bottomright' }).addTo(map);
      mapInstance.current = map;
      markersLayerRef.current = L.layerGroup().addTo(map);
      setMapReady(true);
      setTimeout(() => map.invalidateSize(), 200);
    }
  }, []);

  // Add all branch markers
  useEffect(() => {
    if (!mapReady || !markersLayerRef.current) return;
    markersLayerRef.current.clearLayers();

    branches.forEach(branch => {
      if (branch.lat && branch.lng) {
        const marker = L.marker([branch.lat, branch.lng])
          .bindPopup(`<strong>${branch.city}</strong><br/>${branch.country}`);
        marker.on('click', () => setSelectedBranch(branch));
        markersLayerRef.current.addLayer(marker);
      }
    });
  }, [branches, mapReady]);

  // Fly to selected branch
  useEffect(() => {
    if (!mapInstance.current) return;

    if (!selectedBranch) {
      if (markerRef.current) {
        mapInstance.current.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      return;
    }

    const flyTo = (lat, lng) => {
      mapInstance.current.flyTo([lat, lng], 6, { duration: 1.2 });
      if (markerRef.current) mapInstance.current.removeLayer(markerRef.current);
      markerRef.current = L.marker([lat, lng])
        .addTo(mapInstance.current)
        .bindPopup(`<strong>${selectedBranch.city} Office</strong><br/>${selectedBranch.address}`)
        .openPopup();
    };

    if (selectedBranch.lat && selectedBranch.lng) {
      flyTo(selectedBranch.lat, selectedBranch.lng);
    } else if (selectedBranch.city) {
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(selectedBranch.city + ' ' + (selectedBranch.country || ''))}`)
        .then(r => r.json())
        .then(data => {
          if (data?.[0]) flyTo(parseFloat(data[0].lat), parseFloat(data[0].lon));
        }).catch(() => {});
    }
  }, [selectedBranch]);

  const filtered = branches.filter(b =>
    b.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const refreshBranches = () => {
    if (!effectiveCompanyId) return;
    dispatch(GetBranches({ company_id: effectiveCompanyId })).unwrap().then(res => {
      if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
        const approved = res.data.filter(b => b.approve === 1);
        setBranches(approved);
        setSelectedBranch(approved[0] || null);
      } else {
        setBranches([]);
        setSelectedBranch(null);
      }
    }).catch(() => {
      setBranches([]);
      setSelectedBranch(null);
    });
    if (!readOnly) {
      dispatch(ProfileGet({ userId: effectiveUserId, company_id: effectiveCompanyId }));
    }
  };

  const handleAddBranch = (formData) => {
    dispatch(AddBranch({ ...formData, company_id: effectiveCompanyId, userId: effectiveUserId })).unwrap().then(() => {
      toast.success('Your branch request has been sent successfully! It will be visible after admin approval.');
      refreshBranches();
      setShowAddModal(false);
    }).catch(err => {
      toast.error('Failed to add branch. Please try again.');
      console.error(err);
    });
  };

  const handleEditBranch = (formData) => {
    dispatch(UpdateBranch({ ...formData, id: selectedBranch?.id, company_id: effectiveCompanyId, userId: effectiveUserId })).unwrap().then(() => {
      toast.success('Branch updated successfully!');
      refreshBranches();
      setShowEditModal(false);
    }).catch(err => {
      toast.error('Failed to update branch. Please try again.');
      console.error(err);
    });
  };

  const handleDeleteBranch = (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;
    dispatch(DeleteBranch({ id: branchId, company_id: effectiveCompanyId })).unwrap().then(() => {
      toast.success('Branch deleted successfully!');
      refreshBranches();
    }).catch(err => {
      toast.error('Failed to delete branch.');
      console.error(err);
    });
  };

  const getInitials = (city) => city ? city.substring(0, 2).toUpperCase() : 'BR';

  return (
    <div className="br-page">
      {/* Header */}
      <motion.div className="br-topbar" initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} transition={{duration:0.4}}>
        <div className="br-topbar-left">
          <h1>Branch Offices</h1>
          <p>
            {readOnly ? "Office network" : "Manage your global office network"}
            {branches.length > 0 && (
              <> — {branches.length} {branches.length === 1 ? 'location' : 'locations'} across {[...new Set(branches.map(b => b.country).filter(Boolean))].length} {[...new Set(branches.map(b => b.country).filter(Boolean))].length === 1 ? 'country' : 'countries'}</>
            )}
          </p>
        </div>
        {!readOnly && (
          <button className="br-btn-add" onClick={() => setShowAddModal(true)}>
            <i className="bx bx-plus-circle"></i> New Branch
          </button>
        )}
      </motion.div>

      {/* Map Section */}
      <motion.div
        className="br-map-wrapper"
        initial={{opacity:0, scale:0.98}}
        animate={{opacity:1, scale:1}}
        transition={{duration:0.5, delay:0.1}}
      >
        <div className="br-map-container" ref={mapRef}></div>
        <div className="br-map-overlay">
          <div className="br-map-stat">
            <i className="bx bx-buildings"></i>
            <span>{branches.length} {branches.length === 1 ? 'Office' : 'Offices'}</span>
          </div>
          <div className="br-map-stat">
            <i className="bx bx-globe"></i>
            <span>{[...new Set(branches.map(b => b.country).filter(Boolean))].length} {[...new Set(branches.map(b => b.country).filter(Boolean))].length === 1 ? 'Country' : 'Countries'}</span>
          </div>
        </div>
      </motion.div>

      {/* Layout */}
      <div className="br-layout">
        {/* Sidebar */}
        <motion.aside
          className="br-sidebar"
          initial={{opacity:0, x:-20}}
          animate={{opacity:1, x:0}}
          transition={{duration:0.4, delay:0.2}}
        >
          <div className="br-sidebar-head">
            <h4>Branches</h4>
            <div className="br-sidebar-search">
              <i className="bx bx-search"></i>
              <input placeholder="Search…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="br-list">
            <AnimatePresence>
              {filtered.map((branch, idx) => (
                <motion.div
                  key={branch.id}
                  className={`br-list-item ${selectedBranch?.id === branch.id ? 'br-list-item--active' : ''}`}
                  onClick={() => setSelectedBranch(branch)}
                  initial={{opacity:0, x:-10}}
                  animate={{opacity:1, x:0}}
                  exit={{opacity:0, x:-10}}
                  transition={{delay: idx * 0.04}}
                  whileHover={{x: 4}}
                >
                  <div className="br-list-avatar">{getInitials(branch.city)}</div>
                  <div className="br-list-info">
                    <div className="br-list-city">{branch.city}</div>
                    <div className="br-list-country">{branch.country}</div>
                  </div>
                  {branch.main_branch === 1 && <span className="br-list-badge">HQ</span>}
                </motion.div>
              ))}
            </AnimatePresence>
            {isLoading ? (
              <div className="br-list-empty">
                <i className="bx bx-loader-alt bx-spin"></i>
                <span>Loading branches...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="br-list-empty">
                <i className="bx bx-buildings"></i>
                <span>{branches.length === 0 ? "No branches added yet" : "No branches found"}</span>
              </div>
            ) : null}
          </div>
        </motion.aside>

        {/* Main Detail */}
        <motion.main
          className="br-main"
          initial={{opacity:0, x:20}}
          animate={{opacity:1, x:0}}
          transition={{duration:0.4, delay:0.3}}
        >
          <AnimatePresence mode="wait">
            {selectedBranch ? (
              <motion.div
                key={selectedBranch.id}
                initial={{opacity:0, y:16}}
                animate={{opacity:1, y:0}}
                exit={{opacity:0, y:-16}}
                transition={{duration:0.25}}
              >
                <div className="br-detail-top">
                  <div className="br-detail-title">
                    <div className="br-detail-title-icon">
                      <i className="bx bx-buildings"></i>
                    </div>
                    <div>
                      <h2>{selectedBranch.main_branch === 1 ? 'Head Office' : `${selectedBranch.city} Office`}</h2>
                      <span>{selectedBranch.country} • {selectedBranch.main_branch === 1 ? 'Headquarters' : 'Branch Office'}</span>
                    </div>
                  </div>
                  {!readOnly && (
                    <div className="br-detail-actions">
                      <button className="br-btn-edit" onClick={() => setShowEditModal(true)}>
                        <i className="bx bx-edit-alt"></i> Edit
                      </button>
                    </div>
                  )}
                </div>

                <div className="br-info-grid">
                  <motion.div className="br-info-card" whileHover={{y:-2, boxShadow:'0 8px 24px rgba(0,0,0,0.06)'}}>
                    <div className="br-info-icon"><i className="bx bx-map"></i></div>
                    <div className="br-info-content">
                      <div className="br-info-label">Address</div>
                      <div className="br-info-value">{selectedBranch.address || '—'}</div>
                    </div>
                  </motion.div>
                  <motion.div className="br-info-card" whileHover={{y:-2, boxShadow:'0 8px 24px rgba(0,0,0,0.06)'}}>
                    <div className="br-info-icon"><i className="bx bx-envelope"></i></div>
                    <div className="br-info-content">
                      <div className="br-info-label">Email</div>
                      <div className="br-info-value">{selectedBranch.email || '—'}</div>
                    </div>
                  </motion.div>
                  <motion.div className="br-info-card" whileHover={{y:-2, boxShadow:'0 8px 24px rgba(0,0,0,0.06)'}}>
                    <div className="br-info-icon"><i className="bx bx-phone"></i></div>
                    <div className="br-info-content">
                      <div className="br-info-label">Phone</div>
                      <div className="br-info-value">{selectedBranch.contact || '—'}</div>
                    </div>
                  </motion.div>
                  <motion.div className="br-info-card" whileHover={{y:-2, boxShadow:'0 8px 24px rgba(0,0,0,0.06)'}}>
                    <div className="br-info-icon"><i className="bx bx-globe"></i></div>
                    <div className="br-info-content">
                      <div className="br-info-label">City & Country</div>
                      <div className="br-info-value">{selectedBranch.city}, {selectedBranch.country}</div>
                    </div>
                  </motion.div>
                  <motion.div className="br-info-card" whileHover={{y:-2, boxShadow:'0 8px 24px rgba(0,0,0,0.06)'}}>
                    <div className="br-info-icon"><i className="bx bx-hash"></i></div>
                    <div className="br-info-content">
                      <div className="br-info-label">Country Code</div>
                      <div className="br-info-value">{selectedBranch.country_code || '—'}</div>
                    </div>
                  </motion.div>
                  <motion.div className="br-info-card" whileHover={{y:-2, boxShadow:'0 8px 24px rgba(0,0,0,0.06)'}}>
                    <div className="br-info-icon"><i className="bx bx-badge-check"></i></div>
                    <div className="br-info-content">
                      <div className="br-info-label">Type</div>
                      <div className="br-info-value">{selectedBranch.main_branch === 1 ? 'Headquarters' : 'Branch Office'}</div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ) : (
              <div className="br-main-empty">
                <i className="bx bx-buildings"></i>
                <h4>{isLoading ? "Loading details..." : branches.length === 0 ? "No Branch Offices" : "Select a branch"}</h4>
                <p>
                  {isLoading
                    ? "Fetching branch information..."
                    : branches.length === 0
                      ? (readOnly ? "This company has not added any branch offices yet." : "You haven't added any branch offices yet. Click 'New Branch' to add one.")
                      : "Choose a branch from the list to view details"}
                </p>
              </div>
            )}
          </AnimatePresence>
        </motion.main>
      </div>

      {/* Modals — only in own profile */}
      {!readOnly && (
        <>
          <AddNewBranchModal show={showAddModal} country={profileData?.data?.company_data?.country || ""} onHide={() => setShowAddModal(false)} onSubmit={handleAddBranch} />
          <EditBranchModal show={showEditModal} onHide={() => setShowEditModal(false)} onSubmit={handleEditBranch} oldEmail={selectedBranch?.email} branchData={selectedBranch} />
        </>
      )}
    </div>
  );
}
